import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean; // Legacy support
  requireRole?: "system_owner" | "org_admin" | "employee";
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAdmin = false,
  requireRole,
}) => {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    // Redirect to login page with return url
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Handle new role-based access
  if (requireRole) {
    // Special case: employee route allows both employee and org_admin (but not system_owner)
    if (requireRole === "employee") {
      if (user?.role === "system_owner") {
        // System owners should not access employee route
        return <Navigate to="/system" replace />;
      } else if (user?.role !== "employee" && user?.role !== "org_admin") {
        // Other roles (shouldn't happen, but redirect to employee as fallback)
        return <Navigate to="/employee" replace />;
      }
      // employee or org_admin can access - allow through
    } else if (user?.role !== requireRole) {
      // For other roles, exact match required
      // Redirect based on user's actual role
      if (user?.role === "system_owner") {
        return <Navigate to="/system" replace />;
      } else if (user?.role === "org_admin") {
        return <Navigate to="/admin/dashboard" replace />;
      } else {
        return <Navigate to="/employee" replace />;
      }
    }
  }

  // Legacy admin check (for backward compatibility)
  if (
    requireAdmin &&
    user?.role !== "org_admin" &&
    user?.role !== "system_owner"
  ) {
    return <Navigate to="/employee" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
